/**
 * "Fly to cart" animation: clones the source element to a fixed-position
 * div, animates it toward the floating `[data-cart-trigger]` button, and
 * removes it on completion. Mirrors the tablet's `Restaurant.flyImageToCart`
 * (src/components/Restaurant.js:1146-1196).
 *
 * - Best-effort: silently no-ops if the source or target is missing.
 * - Respects `prefers-reduced-motion`: skips animation entirely.
 */
export const flyImageToCart = (
  sourceEl: Element | null | undefined,
  imagePath?: string,
  productName?: string
): void => {
  if (!sourceEl) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  const target = document.querySelector<HTMLElement>("[data-cart-trigger]");
  if (!target) return;

  const sourceRect = sourceEl.getBoundingClientRect();
  if (sourceRect.width === 0 || sourceRect.height === 0) return;
  const targetRect = target.getBoundingClientRect();

  const clone = document.createElement("div");
  clone.className = "fly-to-cart-clone";
  clone.style.top = `${sourceRect.top}px`;
  clone.style.left = `${sourceRect.left}px`;
  clone.style.width = `${sourceRect.width}px`;
  clone.style.height = `${sourceRect.height}px`;

  if (imagePath) {
    const img = document.createElement("img");
    img.src = imagePath;
    img.alt = "";
    clone.appendChild(img);
  } else {
    const ph = document.createElement("div");
    ph.className = "fly-to-cart-placeholder";
    ph.textContent = (productName || "?").trim().charAt(0).toUpperCase();
    clone.appendChild(ph);
  }

  document.body.appendChild(clone);

  const dx =
    targetRect.left + targetRect.width / 2 -
    (sourceRect.left + sourceRect.width / 2);
  const dy =
    targetRect.top + targetRect.height / 2 -
    (sourceRect.top + sourceRect.height / 2);

  // Two RAFs so the initial style is committed before the transition target.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      clone.style.transform = `translate(${dx}px, ${dy}px) scale(0.15)`;
      clone.style.opacity = "0";
    });
  });

  const cleanup = (): void => {
    if (clone.parentNode) clone.parentNode.removeChild(clone);
  };
  clone.addEventListener("transitionend", cleanup, { once: true });
  // Safety net in case transitionend doesn't fire (tab hidden, fastpath, etc.).
  window.setTimeout(cleanup, 1200);
};
